import {
  login,
  me,
  refresh,
  register,
  logout,
  generateRecoveryCode,
  generateRecoveryCodeEmail,
  verifyRecoveryCode,
  resetPassword,
  changePassword,
  setPassword,
} from '@/api/auth'

/**
 * Состояние модуля аутентификации
 * @returns {Object} - объект состояния
 */
export const state = () => ({
  loggedIn: false,

  accessToken: null,
  refreshToken: null,
  user: null, // добавляем состояние для пользователя
})

/**
 * Мутации для изменения состояния аутентификации
 */
export const mutations = {
  /**
   * Установка статуса авторизации
   * @param {Object} state - состояние store
   * @param {Object} payload - данные с полем status
   */
  loggedIn(state, { status }) {
    state.loggedIn = status
  },

  /**
   * Сохранение токенов в состояние и localStorage
   * @param {Object} state - состояние store
   * @param {Object} payload - объект с accessToken и refreshToken
   */
  setTokens(state, { accessToken, refreshToken }) {
    localStorage.setItem('accessToken', accessToken) // Сохранение токена в localStorage
    localStorage.setItem('refreshToken', refreshToken)
    localStorage.setItem('loggedIn', true)

    state.loggedIn = true
    state.accessToken = accessToken
    state.refreshToken = refreshToken
  },

  /**
   * Сохранение данных пользователя в состояние и localStorage
   * @param {Object} state - состояние store
   * @param {Object} data - данные пользователя
   */
  setUser(state, data) {
    const userData = data?.user ? data?.user : data

    const user = structuredClone(userData)
    user.roles = data?.roles || []

    state.user = user
    localStorage.setItem('user', JSON.stringify(user))
  },

  /**
   * Очистка токенов и данных пользователя
   * @param {Object} state - состояние store
   */
  clearTokens(state) {
    state.loggedIn = false
    state.accessToken = null
    state.refreshToken = null
    state.user = null // очищаем данные пользователя при выходе

    localStorage.setItem('loggedIn', false)
    localStorage.removeItem('user') // Удаление пользователя из localStorage
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  },
}

/**
 * Действия для работы с аутентификацией
 */
export const actions = {
  /**
   * Получение данных текущего пользователя
   * @param {Object} context - контекст Vuex (commit)
   */
  async getUser({ commit }) {
    const userData = await me()
    commit('setUser', userData)
    return userData
  },

  /**
   * Регистрация нового пользователя
   * @param {Object} context - контекст Vuex (commit)
   * @param {Object} userData - данные пользователя для регистрации
   * @returns {Promise} - промис с результатом регистрации
   */
  async register({ commit }, userData) {
    const response = await register(userData)

    if (response.error) {
      const error = new Error('Registration error')
      error.response = { data: response }
      throw error
    }

    commit('setUser', response)
    commit('setTokens', {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    })
    return response
  },

  /**
   * Вход пользователя в систему
   * @param {Object} context - контекст Vuex (commit)
   * @param {Object} credentials - данные для входа (phone, password)
   */
  async login({ commit }, credentials) {
    const res = await login(credentials)
    if (!res.error) {
      commit('setTokens', {
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
      })

      const userData = await me()
      commit('setUser', userData)
    } else {
      commit('clearTokens')
    }
  },

  /**
   * Генерация кода восстановления пароля
   * @param {Object} context - контекст Vuex (commit)
   * @param {string} email - email пользователя
   * @returns {Promise} - промис с результатом генерации кода
   */
  async generateRecoveryCode({ commit }, email) {
    try {
      const response = await generateRecoveryCode({ email })
      return response
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || 'Ошибка генерации кода восстановления'
      )
    }
  },

  async generateRecoveryCodeEmail({ commit }, email) {
    try {
      const response = await generateRecoveryCodeEmail({ email })
      return response
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || 'Ошибка генерации кода восстановления'
      )
    }
  },

  /**
   * Проверка кода восстановления пароля
   * @param {Object} context - контекст Vuex (commit)
   * @param {Object} payload - объект с email и code
   * @returns {Promise} - промис с результатом проверки кода
   */
  async verifyRecoveryCode({ commit }, { email, code }) {
    try {
      const response = await verifyRecoveryCode({ email, code })
      return response
    } catch (error) {
      throw new Error(error?.response?.data?.message || 'Ошибка проверки кода')
    }
  },

  /**
   * Сброс пароля по коду восстановления
   * @param {Object} context - контекст Vuex (commit)
   * @param {Object} payload - объект с email, code и newPassword
   * @returns {Promise} - промис с результатом сброса пароля
   */
  async resetPassword({ commit }, { email, code, newPassword }) {
    try {
      const response = await resetPassword({ email, code, newPassword })
      return response
    } catch (error) {
      throw new Error(error?.response?.data?.message || 'Ошибка смены пароля')
    }
  },

  /**
   * Смена пароля через личный кабинет
   * @param {Object} context - контекст Vuex (commit)
   * @param {Object} payload - объект с currentPassword, newPassword и userId
   * @returns {Promise} - промис с результатом смены пароля
   */
  async changePassword({ commit }, { currentPassword, newPassword, userId }) {
    try {
      const response = await changePassword({
        currentPassword,
        newPassword,
        userId,
      })
      return response
    } catch (error) {
      throw new Error(error?.response?.data?.message || 'Ошибка смены пароля')
    }
  },

  /**
   * Замена временного пароля на постоянный и обновление пользователя (снимается mustChangePassword)
   * @param {Object} context - контекст Vuex (dispatch)
   * @param {Object} payload - объект с newPassword
   */
  async setPassword({ dispatch }, { newPassword }) {
    const response = await setPassword({ newPassword })
    await dispatch('getUser')
    return response
  },

  /**
   * Обновление токена доступа
   * @param {Object} context - контекст Vuex (commit, state)
   */
  async refreshToken({ commit, state }) {
    const response = await refresh({
      token: state.refreshToken,
    })
    commit('setTokens', {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken || state.refreshToken,
    })

    const userData = await me()
    commit('setUser', userData)
  },

  /**
   * Выход пользователя из системы
   * @param {Object} context - контекст Vuex (commit, state)
   */
  async logout({ commit, state }) {
    try {
      await logout({ refreshToken: state.refreshToken })
    } catch (error) {
      // Игнорируем ошибки при логауте на сервере
    } finally {
      // В любом случае очищаем токены локально
      commit('clearTokens')
    }
  },

  /**
   * Установка статуса авторизации
   * @param {Object} context - контекст Vuex (commit)
   * @param {boolean} status - статус авторизации
   */
  loggedIn({ commit }, status) {
    commit('loggedIn', { status })
  },

  /**
   * Загрузка данных аутентификации из localStorage при инициализации
   * @param {Object} context - контекст Vuex (commit)
   */
  loadAuthData({ commit }) {
    const user = JSON.parse(localStorage.getItem('user'))
    const accessToken = localStorage.getItem('accessToken')
    const refreshToken = localStorage.getItem('refreshToken')

    if (user && accessToken && refreshToken) {
      commit('setUser', user)
      commit('setTokens', { accessToken, refreshToken })
      commit('loggedIn', { status: true })
    } else {
      // Если токены отсутствуют, очищаем состояние
      commit('clearTokens')
    }
  },
}

/**
 * Геттеры для получения данных из состояния аутентификации
 */
export const getters = {
  /**
   * Проверка статуса авторизации пользователя
   * @param {Object} state - состояние store
   * @returns {boolean} - true если пользователь авторизован
   */
  loggedIn(state) {
    // Проверяем наличие токенов в state или localStorage
    const hasTokens = state.accessToken && state.refreshToken
    const hasLocalTokens =
      localStorage.getItem('accessToken') &&
      localStorage.getItem('refreshToken')
    return hasTokens || hasLocalTokens
  },

  /**
   * Получение данных текущего пользователя
   * @param {Object} state - состояние store
   * @returns {Object|null} - данные пользователя или null
   */
  getUser(state) {
    return state.user
  },

  /**
   * Пользователь вошёл с временным паролем и обязан сменить его
   * @param {Object} state - состояние store
   * @returns {boolean}
   */
  mustChangePassword(state) {
    return state.user?.mustChangePassword === true
  },
}
